import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import { WorkspaceFile } from '../client/GatewayClient';

interface WorkspaceNavContextValue {
  navCollapsed: boolean;
  toggleNav: () => void;
  setNavCollapsed: (collapsed: boolean) => void;
  /** Currently selected workspace file — persists across route changes */
  selectedFile: WorkspaceFile | null;
  setSelectedFile: (file: WorkspaceFile | null) => void;
  /** Increment to trigger WorkspaceNav to re-fetch file list */
  refreshTrigger: number;
  /** Call to request a workspace file list refresh */
  refreshWorkspace: () => void;
  /** File key to auto-navigate to after next refresh completes */
  pendingNavigateKey: string | null;
  setPendingNavigateKey: (key: string | null) => void;
}

// Exported so tests can inject spy values (e.g. a vi.fn() refreshWorkspace) via
// <WorkspaceNavContext.Provider>; production code uses WorkspaceNavProvider below.
export const WorkspaceNavContext = createContext<WorkspaceNavContextValue>({
  navCollapsed: false,
  toggleNav: () => {},
  setNavCollapsed: () => {},
  selectedFile: null,
  setSelectedFile: () => {},
  refreshTrigger: 0,
  refreshWorkspace: () => {},
  pendingNavigateKey: null,
  setPendingNavigateKey: () => {},
});

export function WorkspaceNavProvider({ children }: { children: ReactNode }) {
  const [navCollapsed, setNavCollapsed] = useState(false);
  const toggleNav = useCallback(() => setNavCollapsed(prev => !prev), []);
  const [selectedFile, setSelectedFile] = useState<WorkspaceFile | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [pendingNavigateKey, setPendingNavigateKey] = useState<string | null>(null);

  const refreshWorkspace = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const value = useMemo(
    () => ({
      navCollapsed, toggleNav, setNavCollapsed,
      selectedFile, setSelectedFile,
      refreshTrigger, refreshWorkspace,
      pendingNavigateKey, setPendingNavigateKey,
    }),
    [navCollapsed, toggleNav, selectedFile, refreshTrigger, refreshWorkspace, pendingNavigateKey],
  );

  return (
    <WorkspaceNavContext.Provider value={value}>
      {children}
    </WorkspaceNavContext.Provider>
  );
}

export function useWorkspaceNav() {
  return useContext(WorkspaceNavContext);
}
